import { format, addHours } from 'date-fns';

import type { PortfolioExercise } from 'global/Securities/types/store/portfolio';
import {
  OrderForceSellType,
  SecuritiesKind,
  OrderSecuritiesKindType,
} from 'global/Securities/types/store/order';
import {
  ForceSellEvent,
  OrderDateTimestamp,
} from '../../../@types/securities/websocket/orderWS';

const HOUR_OFFSET = -7;

export const convertBooleanToNumber = (bool: boolean): number => (bool ? 1 : 0);

export const formatToLocalDate = (date: Date, customFormat: string): string => {
  const adjustedDate = addHours(date, HOUR_OFFSET);
  const formattedHour = format(adjustedDate, customFormat);
  return formattedHour;
};

export const convertTimestampToDate = (time: string | number): string => {
  if (!time) return '';

  const jsDate = new Date(time);
  return formatToLocalDate(jsDate, 'yyyy-MM-dd HH:mm:ss');
};

export const convertGoogleDateToTimestamp = (date: PortfolioDate): string => {
  if (!date) return '';

  const { year, month, day } = date;
  const jsDate = new Date(year, month - 1, day + 1);
  return formatToLocalDate(jsDate, 'dd-MM-yyyy');
};

export const getHourFromTimestamp = (time: string | number): string => {
  if (!time) return '';

  const jsDate = new Date(time);
  return formatToLocalDate(jsDate, 'HH:mm:ss');
};

export const getTime = (timestamp: OrderDateTimestamp): string | number => {
  if (!timestamp) return '';

  return Number(timestamp.seconds) * 1000;
};

export const getExercisedData = (
  exercise: PortfolioItemData['exercise'],
): PortfolioExercise => {
  if (!exercise) return null;

  const { active, type, price, info, date } = exercise;

  return {
    active,
    type,
    price,
    info,
    listing_date: convertGoogleDateToTimestamp(date?.listing),
    expired_date: convertGoogleDateToTimestamp(date?.expired),
    mature_date: convertGoogleDateToTimestamp(date?.mature),
    exercise_start_date: convertGoogleDateToTimestamp(date?.start),
    exercise_end_date: convertGoogleDateToTimestamp(date?.end),
  };
};

export const convertForceSellEvent = (
  event: ForceSellEvent,
): OrderForceSellType => {
  switch (event) {
    case 1:
      return OrderForceSellType.DebtRatio;
    case 2:
      return OrderForceSellType.TimeLimit;
    default:
      return OrderForceSellType.Unspecified;
  }
};

export const convertSmartOrderConfigType = (configType: string): string => {
  switch (configType) {
    case 'CONFIG_PRICE_TYPE_ABOVE':
      return '1';
    case 'CONFIG_PRICE_TYPE_BELOW':
      return '2';
    default:
      return configType;
  }
};

export const convertSecuritiesKind = (
  kind: SecuritiesKind,
): OrderSecuritiesKindType => {
  switch (kind) {
    case SecuritiesKind.STOCK:
      return OrderSecuritiesKindType.Stock;
    case SecuritiesKind.BOND:
      return OrderSecuritiesKindType.Bond;
    default:
      return OrderSecuritiesKindType.Unspecified;
  }
};

export const lotFormatter = (value: number) => Intl.NumberFormat('en-US').format(value);
